import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Button, Space, Modal, message, Typography, Tabs, Statistic, Row, Col } from 'antd';
import { FlagOutlined, EyeInvisibleOutlined, CheckOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Text, Title } = Typography;

const TYPE_LABEL = {
  team_news: 'Notícia de time',
  friendly: 'Amistoso',
  match: 'Jogo',
  player: 'Perfil de jogador',
  team: 'Time',
};

export default function AdminModeration() {
  const [status, setStatus] = useState('open');
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({ open_generic: 0, open_news: 0, resolved_generic: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/admin/moderation/reports', { params: { status } }),
        api.get('/admin/moderation/stats'),
      ]);
      setReports(r.data?.reports || []);
      setStats(s.data || stats);
    } catch { message.error('Erro ao carregar'); }
    setLoading(false);
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const resolve = (row, action) => {
    Modal.confirm({
      title: action === 'hide' ? 'Esconder este conteúdo?' : 'Descartar denúncia?',
      content: action === 'hide'
        ? 'O conteúdo vai sumir do feed. Autor pode reagir (recurso). Ação registrada com seu usuário.'
        : 'Denúncia marcada como não procedente. Conteúdo continua no ar.',
      okText: action === 'hide' ? 'Esconder' : 'Descartar',
      okType: action === 'hide' ? 'danger' : 'default',
      cancelText: 'Voltar',
      onOk: async () => {
        try {
          await api.patch(`/admin/moderation/reports/${row.id}`, { source: row.source, action });
          message.success(action === 'hide' ? 'Escondido' : 'Descartado');
          load();
        } catch { message.error('Erro'); }
      },
    });
  };

  const columns = [
    { title: 'Quando', dataIndex: 'created_at', width: 140,
      render: v => new Date(v).toLocaleString('pt-BR') },
    { title: 'Tipo', dataIndex: 'target_type', width: 140,
      render: v => <Tag color="orange">{TYPE_LABEL[v] || v}</Tag> },
    { title: 'Alvo', key: 'target',
      render: (_, r) => (
        <div>
          <div><Text code>#{r.target_id}</Text></div>
          {r.news_text && (
            <div style={{ fontSize: 12, color: '#5a6a63', marginTop: 4, fontStyle: 'italic', maxWidth: 400 }}>
              "{r.news_text.slice(0, 140)}{r.news_text.length > 140 ? '...' : ''}"
              {r.news_team_slug && (
                <> · <a href={`https://comunidade.futezap.com.br/time/${r.news_team_slug}`} target="_blank" rel="noreferrer">
                  {r.news_team_name}
                </a></>
              )}
            </div>
          )}
        </div>
      ) },
    { title: 'Denunciante', dataIndex: 'reporter_name', width: 160,
      render: (v, r) => r.reporter_slug
        ? <a href={`https://comunidade.futezap.com.br/jogador/${r.reporter_slug}`} target="_blank" rel="noreferrer">{v}</a>
        : v },
    { title: 'Motivo', dataIndex: 'reason', ellipsis: true,
      render: v => v || <Text type="secondary" style={{ fontSize: 11 }}>sem motivo</Text> },
    { title: 'Status', dataIndex: 'resolved_at', width: 130,
      render: (v, r) => v
        ? <Tag color={r.resolution === 'hide' ? 'red' : 'default'}>{r.resolution === 'hide' ? 'Escondido' : r.resolution === 'hidden_auto' ? 'Auto-escondido' : 'Descartado'}</Tag>
        : <Tag color="gold">Aberta</Tag> },
    { title: 'Ações', key: 'actions', width: 220, fixed: 'right',
      render: (_, r) => r.resolved_at ? null : (
        <Space>
          <Button size="small" icon={<CheckOutlined />} onClick={() => resolve(r, 'dismiss')}>Descartar</Button>
          <Button size="small" danger icon={<EyeInvisibleOutlined />} onClick={() => resolve(r, 'hide')}>Esconder</Button>
        </Space>
      ) },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}><FlagOutlined /> Moderação</Title>
        <Button icon={<ReloadOutlined />} onClick={load}>Atualizar</Button>
      </div>

      <Row gutter={12} style={{ marginBottom: 20 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Denúncias abertas (genéricas)" value={stats.open_generic}
              valueStyle={{ color: stats.open_generic > 0 ? '#ff4d4f' : '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Notícias c/ denúncia aberta" value={stats.open_news}
              valueStyle={{ color: stats.open_news > 0 ? '#faad14' : '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Já resolvidas" value={stats.resolved_generic}
              valueStyle={{ color: '#8c8c8c' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={status} onChange={setStatus}
          items={[
            { key: 'open',     label: 'Abertas' },
            { key: 'resolved', label: 'Resolvidas' },
            { key: 'all',      label: 'Todas' },
          ]}
        />
        <Table
          rowKey={(r) => `${r.source}-${r.id}`}
          columns={columns}
          dataSource={reports}
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
}
